import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Context and Hooks
import { AlertContext } from "@context/AlertProvider";
import { useChallengeUpdateMutation } from "@mutation/challengeMutation";
import { useChallengeQuery } from "@query/challengeQuery";
import { getApiBaseUrl } from "@config/config"

// Components
import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartContainer from './smartForm/SmartContainer';
import SmartDateTimeInput from "./smartForm/SmartDateTimeInput";
import SmartTextareaInput from "./smartForm/SmartTextareaInput";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

import CopyableLink from "@component/CopyableLink";

// Utils and Services
import { removeEmptyValues } from '@utils/generalUtils';
import { mapChallengeType } from "@service/challengeService";
import {
  createNumberMinValidator,
  createLengthInRangeValidator,
} from "@component/smartForm/fieldValidators";

function ChallengeForm({ uuid, onSave, onCancel }) {
  // Validators
  const nameValidator = createLengthInRangeValidator(3, 50);
  const initBankValidator = createNumberMinValidator(0);

  // Hooks
  const challengeQuery = useChallengeQuery(uuid, false);
  const challengeUpdateMutation = useChallengeUpdateMutation(uuid);
  const { addAlertBanner } = useContext(AlertContext);
  const { t } = useTranslation(["main", "challenges", "error"]);
  const theme = useTheme();

  // State
  const [challengeModel, setChallengeModel] = useState();

  // Effects
  useEffect(() => {
    setChallengeModel(undefined); // Reset the model when uuid changes
  }, [uuid]);

  useEffect(() => {
    if (challengeQuery.data?.data) {
      const challenge = challengeQuery.data.data;
      setChallengeModel(challenge);
    }
  }, [challengeQuery.data]);

  // Handlers
  const handleSave = (newModel) => {
    console.log(newModel);
    const cleaned = removeEmptyValues({
      name: newModel.name,
      description: newModel.description,
      initial_bank: newModel.initial_bank,
    });

    challengeUpdateMutation.mutate({ data: cleaned }, {
      onSuccess: () => {
        addAlertBanner(t("main:saveSuccess"), "success", 4000);
        onSave?.();
      },
      onError: (error) => {
        if (!error.response) {
          addAlertBanner(t("error:networkError"), "error", 4000);
          return;
        }

        const statusCode = error.response.status;

        if (statusCode === 403) {
          addAlertBanner(t("error:noModificationPrivileges"), "error", 5000);
        } else if (statusCode === 404) {
          addAlertBanner(t("error:notFound"), "error", 4000);
        } else if (statusCode === 400) {
          addAlertBanner(t("error:validationError"), "error", 5000);
        } else if (statusCode === 422) {
          addAlertBanner(t("error:validationError"), "error", 5000);
        } else if (statusCode >= 500) {
          addAlertBanner(t("error:serverError"), "error", 4000);
        } else {
          addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
        }
      }
    });
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // Helper functions
  const getSelectedHostingTarget = () => {
    if (challengeModel?.hosting_club) {
      return (
        <SmartTextInput
          value={challengeModel?.hosting_club.name}
          label={t("main:club")}
          editable={false}
          sx={{ flex: 1 }}
        />
      )
    } else if (challengeModel?.hosting_site) {
      return (
        <SmartTextInput
          value={challengeModel?.hosting_site.name}
          label={t("main:site")}
          editable={false}
          sx={{ flex: 1 }}
        />
      )
    }
  }

  const getChallengeTypeTranslationKey = () => {
    if (mapChallengeType(challengeModel?.type?.name)) {
      return mapChallengeType(challengeModel?.type?.name)['translationKey'];
    }
    return null;
  }

  const getChallengeTypeDescriptionKey = () => {
    if (mapChallengeType(challengeModel?.type?.name)) {
      return mapChallengeType(challengeModel?.type?.name)['descriptionKey'];
    }
    return null;
  }

  return (
    <SmartForm
      formModel={challengeModel}
      setFormModel={setChallengeModel}
      onSave={handleSave}
      onCancel={handleCancel}
      isLoading={challengeQuery.isLoading}
      buttonPanelSx={{
        position: "initial",
        p: 1,
        flexDirection: "row-reverse"
      }}
    >
      {/* Name and Description Section */}
      <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SmartTextInput
          field="name"
          label={t("challenges:name")}
          valueSx={{ fontSize: theme.typography.h5 }}
          validator={nameValidator}
        />
        <SmartTextareaInput
          field="description"
          label={t("challenges:description")}
          rows={3}
        />
      </SmartContainer>

      {/* Date Section */}
      <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <SmartDateTimeInput
          sx={{ flex: 1 }}
          editable={false}
          startIcon={<CalendarMonthIcon />}
          field="start"
          label={t("challenges:start")}
          ampm={false}
        />
        <SmartDateTimeInput
          sx={{ flex: 1 }}
          editable={false}
          startIcon={<CalendarMonthIcon />}
          field="end"
          label={t("challenges:end")}
          ampm={false}
        />
      </SmartContainer>

      {/* Bank and Hosting Section */}
      <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <SmartTextInput
          field="initial_bank"
          label={t("challenges:initialBank")}
          sx={{ flex: 1 }}
          validator={initBankValidator}
        />
        {getSelectedHostingTarget()}
      </SmartContainer>

      {/* Challenge Type Section */}
      <SmartContainer sx={{ flex: 1, p: 1 }} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SmartTextInput
          value={t(getChallengeTypeTranslationKey())}
          label={t("challenges:type")}
          editable={false}
          sx={{ flex: 1 }}
        />
        <SmartTextareaInput
          value={t(getChallengeTypeDescriptionKey())}
          editable={false}
          label={t("challenges:typeDescription")}
          rows={3}
        />
      </SmartContainer>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        p: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "grey.50",
        borderRadius: 2,
        mx: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          borderColor: "primary.light",
        }
      }}>

        <Box sx={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          alignItems:"center",
          gap:2,
          minWidth: 0, // Important for text truncation
        }}>
          <LeaderboardIcon
            fontSize="medium"
            sx={{
              color: "primary.main",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Chcete zobrazit žebříček výzvy?
          </Typography>
        </Box>
        <CopyableLink link={`${window.location.protocol}//${window.location.hostname}/leaderboard/${uuid}`} />
      </Box>
    </SmartForm>
  );
}

export default ChallengeForm;